/**
 * Offline-First Sync Service
 * Uses IndexedDB for offline queue management
 */

const DB_NAME = 'sfarm_offline';
const DB_VERSION = 1;
const STORES = {
  QUEUE: 'sync_queue',
  PHOTOS: 'photo_queue'
};

let dbInstance = null;

// Initialize IndexedDB
const initDB = () => {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Sync queue for general items (scouting points, activities)
      if (!db.objectStoreNames.contains(STORES.QUEUE)) {
        const queueStore = db.createObjectStore(STORES.QUEUE, { keyPath: 'id', autoIncrement: true });
        queueStore.createIndex('type', 'type', { unique: false });
        queueStore.createIndex('status', 'status', { unique: false });
        queueStore.createIndex('createdAt', 'createdAt', { unique: false });
      }
      
      // Photo queue for blob storage
      if (!db.objectStoreNames.contains(STORES.PHOTOS)) {
        const photoStore = db.createObjectStore(STORES.PHOTOS, { keyPath: 'id', autoIncrement: true });
        photoStore.createIndex('parentId', 'parentId', { unique: false });
        photoStore.createIndex('status', 'status', { unique: false });
      }
    };
  });
};

// Add item to sync queue
export const addToQueue = async (type, data, parentId = null) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.QUEUE);
    
    const item = {
      type, // 'scouting_point', 'activity', 'task'
      data,
      parentId,
      status: 'pending',
      retries: 0,
      createdAt: new Date().toISOString(),
      localId: `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    
    const request = store.add(item);
    request.onsuccess = () => resolve({ ...item, id: request.result });
    request.onerror = () => reject(request.error);
  });
};

// Add photo to queue
export const addPhotoToQueue = async (blob, parentId, metadata = {}) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PHOTOS], 'readwrite');
    const store = transaction.objectStore(STORES.PHOTOS);
    
    const item = {
      blob,
      parentId,
      metadata,
      status: 'pending',
      retries: 0,
      createdAt: new Date().toISOString(),
      localId: `photo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    
    const request = store.add(item);
    request.onsuccess = () => resolve({ ...item, id: request.result });
    request.onerror = () => reject(request.error);
  });
};

// Get all pending items
export const getPendingItems = async () => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.QUEUE], 'readonly');
    const store = transaction.objectStore(STORES.QUEUE);
    const index = store.index('status');
    
    const request = index.getAll('pending');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Get pending photos
export const getPendingPhotos = async () => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PHOTOS], 'readonly');
    const store = transaction.objectStore(STORES.PHOTOS);
    const index = store.index('status');
    
    const request = index.getAll('pending');
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
};

// Update item status
export const updateItemStatus = async (id, status, serverId = null) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.QUEUE);
    
    const getRequest = store.get(id);
    getRequest.onsuccess = () => {
      const item = getRequest.result;
      if (item) {
        item.status = status;
        if (serverId) item.serverId = serverId;
        if (status === 'failed') item.retries += 1;
        
        const putRequest = store.put(item);
        putRequest.onsuccess = () => resolve(item);
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        reject(new Error('Item not found'));
      }
    };
  });
};

// Update photo status
export const updatePhotoStatus = async (id, status, storagePath = null) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PHOTOS], 'readwrite');
    const store = transaction.objectStore(STORES.PHOTOS);
    
    const getRequest = store.get(id);
    getRequest.onsuccess = () => {
      const item = getRequest.result;
      if (item) {
        item.status = status;
        if (storagePath) item.storagePath = storagePath;
        if (status === 'failed') item.retries += 1;
        
        const putRequest = store.put(item);
        putRequest.onsuccess = () => resolve(item);
        putRequest.onerror = () => reject(putRequest.error);
      } else {
        reject(new Error('Photo not found'));
      }
    };
  });
};

// Delete synced items
export const deleteItem = async (id) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.QUEUE], 'readwrite');
    const store = transaction.objectStore(STORES.QUEUE);
    
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// Delete photo
export const deletePhoto = async (id) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.PHOTOS], 'readwrite');
    const store = transaction.objectStore(STORES.PHOTOS);
    
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// Get sync status counts
export const getSyncStatus = async () => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.QUEUE, STORES.PHOTOS], 'readonly');
    
    let pendingItems = 0;
    let pendingPhotos = 0;
    let failedItems = 0;
    
    const queueStore = transaction.objectStore(STORES.QUEUE);
    const photoStore = transaction.objectStore(STORES.PHOTOS);
    
    const queueCursor = queueStore.openCursor();
    queueCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        if (cursor.value.status === 'pending') pendingItems++;
        if (cursor.value.status === 'failed') failedItems++;
        cursor.continue();
      }
    };
    
    const photoCursor = photoStore.openCursor();
    photoCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        if (cursor.value.status === 'pending') pendingPhotos++;
        cursor.continue();
      }
    };
    
    transaction.oncomplete = () => {
      resolve({
        pendingItems,
        pendingPhotos,
        failedItems,
        total: pendingItems + pendingPhotos
      });
    };
    
    transaction.onerror = () => reject(transaction.error);
  });
};

// Get items by local ID
export const getItemByLocalId = async (localId) => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.QUEUE], 'readonly');
    const store = transaction.objectStore(STORES.QUEUE);
    
    const request = store.openCursor();
    request.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        if (cursor.value.localId === localId) {
          resolve(cursor.value);
          return;
        }
        cursor.continue();
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
};

// Clear all synced items
export const clearSyncedItems = async () => {
  const db = await initDB();
  
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.QUEUE, STORES.PHOTOS], 'readwrite');
    
    const queueStore = transaction.objectStore(STORES.QUEUE);
    const photoStore = transaction.objectStore(STORES.PHOTOS);
    
    // Delete synced items from queue
    const queueCursor = queueStore.openCursor();
    queueCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        if (cursor.value.status === 'synced') {
          cursor.delete();
        }
        cursor.continue();
      }
    };
    
    // Delete synced photos
    const photoCursor = photoStore.openCursor();
    photoCursor.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        if (cursor.value.status === 'synced') {
          cursor.delete();
        }
        cursor.continue();
      }
    };
    
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
};

export default {
  addToQueue,
  addPhotoToQueue,
  getPendingItems,
  getPendingPhotos,
  updateItemStatus,
  updatePhotoStatus,
  deleteItem,
  deletePhoto,
  getSyncStatus,
  getItemByLocalId,
  clearSyncedItems
};
